// ═══════════════════════════════════════════════════════
// UPLOAD PROMPT SCREEN — Onboarding file upload
// ═══════════════════════════════════════════════════════
import { useState } from "react";
import { RADIUS, TYPE, SP, LIGHT, TRANSITION } from "../shared/theme.js";
import { uploadExtrato } from "../supabaseClient.js";

interface UploadPromptScreenProps {
  clientName: string;
  clientId: string;
  userId?: string;
  onDone: () => void;
  T?: any;
  suitabilityDone?: boolean;
}

export default function UploadPromptScreen({
  clientName,
  clientId,
  userId: _userId,
  onDone,
  T: _T,
  suitabilityDone = false,
}: UploadPromptScreenProps) {
  const [dragging, setDragging] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [fileStatuses, setFileStatuses] = useState<Record<string, string>>({});
  const firstName = (clientName || "").split(" ")[0] || "Cliente";

  const handleFiles = (incoming: FileList) => {
    const arr = Array.from(incoming).filter((f) => /\.(pdf|csv|xlsx|xls|ofx)$/i.test(f.name));
    setFiles((prev) => {
      const names = prev.map((f) => f.name);
      return [...prev, ...arr.filter((f) => !names.includes(f.name))];
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleSend = async () => {
    if (!files.length) {
      onDone();
      return;
    }
    setSending(true);

    const statuses: Record<string, string> = {};
    files.forEach((f) => {
      statuses[f.name] = "uploading";
    });
    setFileStatuses({ ...statuses });

    let errorCount = 0;
    let successCount = 0;
    for (const file of files) {
      try {
        await uploadExtrato(clientId, file);
        statuses[file.name] = "done";
        successCount++;
      } catch (e: any) {
        statuses[file.name] = `error: ${e.message}`;
        errorCount++;
      }
      setFileStatuses({ ...statuses });
    }

    setSending(false);
    setSentCount(successCount);

    if (successCount === 0) return;
    setTimeout(() => setSent(true), errorCount > 0 ? 1500 : 400);
  };

  const STEPS = [
    { label: "Questionário de perfil", done: suitabilityDone, skipped: !suitabilityDone },
    { label: "Envio de extratos", done: false, active: true },
    { label: "Portal disponível", done: false },
  ];

  const totalFiles = files.length;
  const failedCount = totalFiles - sentCount;
  const allOk = failedCount === 0;

  if (sent)
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg,#0B2447 0%,#19376D 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Inter',sans-serif",
        }}
      >
        <div style={{ textAlign: "center", color: "#fff", maxWidth: 380, padding: 24 }}>
          <div style={{ fontSize: TYPE["3xl"], marginBottom: SP[4] }}>{allOk ? "✅" : "⚠️"}</div>
          <div style={{ fontSize: TYPE["2xl"], fontWeight: TYPE.heavy, marginBottom: SP[2] }}>
            {allOk
              ? "Extratos recebidos!"
              : `${sentCount} de ${totalFiles} arquivo${sentCount > 1 ? "s" : ""} enviado${sentCount > 1 ? "s" : ""}!`}
          </div>
          {allOk ? (
            <div style={{ fontSize: TYPE.md, color: "rgba(255,255,255,0.7)", marginBottom: SP[7] }}>
              Nossa equipe irá processar e atualizar sua posição em breve.
            </div>
          ) : (
            <div
              style={{
                fontSize: TYPE.base,
                color: "rgba(255,200,50,0.9)",
                marginBottom: SP[7],
                lineHeight: TYPE.normal,
              }}
            >
              {failedCount} arquivo{failedCount > 1 ? "s" : ""} não pud
              {failedCount > 1 ? "eram" : "e"} ser enviado{failedCount > 1 ? "s" : ""}. Você pode
              reenviar depois pelo portal ou entrar em contato com nosso time.
            </div>
          )}
          <button
            onClick={onDone}
            style={{
              background: LIGHT.gold,
              color: LIGHT.navy,
              border: "none",
              borderRadius: RADIUS.md,
              padding: `${SP[3]} ${SP[8]}`,
              fontWeight: TYPE.heavy,
              fontSize: TYPE.lg,
              cursor: "pointer",
            }}
          >
            Acessar o Portal →
          </button>
        </div>
      </div>
    );

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg,#0B2447 0%,#19376D 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Inter',sans-serif",
        padding: 24,
      }}
    >
      {/* Logo */}
      <div style={{ display: "flex", alignItems: "center", gap: SP[2], marginBottom: SP[8] }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: RADIUS.sm,
            background: LIGHT.gold,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: TYPE.heavy,
            fontSize: TYPE.lg,
            color: LIGHT.navy,
          }}
        >
          L
        </div>
        <span
          style={{ color: LIGHT.gold, fontWeight: TYPE.heavy, fontSize: TYPE.lg, letterSpacing: 1 }}
        >
          LPA WEALTH
        </span>
      </div>

      {/* Stepper */}
      <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: SP[9] }}>
        {STEPS.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center" }}>
            <div
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: SP[1] }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: RADIUS.full,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: s.skipped ? TYPE.sm : TYPE.md,
                  fontWeight: TYPE.heavy,
                  background: s.done
                    ? LIGHT.gold
                    : s.skipped
                      ? "rgba(255,255,255,0.05)"
                      : s.active
                        ? "rgba(201,162,39,0.25)"
                        : "rgba(255,255,255,0.1)",
                  color: s.done
                    ? LIGHT.navy
                    : s.skipped
                      ? "rgba(255,255,255,0.25)"
                      : s.active
                        ? LIGHT.gold
                        : "rgba(255,255,255,0.4)",
                  border: s.active
                    ? `2px solid ${LIGHT.gold}`
                    : s.skipped
                      ? "2px dashed rgba(255,255,255,0.15)"
                      : "2px solid transparent",
                }}
              >
                {s.done ? "✓" : s.skipped ? "–" : i + 1}
              </div>
              <span
                style={{
                  fontSize: TYPE.xs,
                  whiteSpace: "nowrap",
                  maxWidth: 90,
                  textAlign: "center",
                  lineHeight: TYPE.tight,
                  color: s.done
                    ? "rgba(255,255,255,0.9)"
                    : s.skipped
                      ? "rgba(255,255,255,0.25)"
                      : s.active
                        ? "rgba(255,255,255,0.9)"
                        : "rgba(255,255,255,0.4)",
                  fontStyle: s.skipped ? "italic" : "normal",
                }}
              >
                {s.label}
                {s.skipped ? " (pulado)" : ""}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                style={{
                  width: 48,
                  height: 2,
                  background: s.done ? LIGHT.gold : "rgba(255,255,255,0.15)",
                  margin: `0 ${SP[2]}`,
                  marginBottom: SP[6],
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Card */}
      <div
        style={{
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: RADIUS.lg,
          padding: `${SP[8]} ${SP[9]}`,
          maxWidth: 520,
          width: "100%",
          backdropFilter: "blur(10px)",
        }}
      >
        <div
          style={{
            fontSize: TYPE["2xl"],
            fontWeight: TYPE.heavy,
            color: "#fff",
            marginBottom: SP[1],
          }}
        >
          Quase lá, {firstName}! 🎉
        </div>
        <div
          style={{
            fontSize: TYPE.md,
            color: "rgba(255,255,255,0.65)",
            marginBottom: SP[6],
            lineHeight: TYPE.normal,
          }}
        >
          Para visualizarmos sua posição consolidada atualizada, envie os extratos do último período
          das suas corretoras. Aceitamos arquivos das plataformas{" "}
          <strong style={{ color: "rgba(255,255,255,0.85)" }}>
            XP Investimentos, BTG Pactual, Banco Inter e Avenue Securities
          </strong>
          .
        </div>

        {/* Drop zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${dragging ? LIGHT.gold : "rgba(255,255,255,0.2)"}`,
            borderRadius: RADIUS.md,
            padding: `${SP[7]} ${SP[5]}`,
            textAlign: "center",
            cursor: "pointer",
            transition: TRANSITION.fast,
            background: dragging ? "rgba(201,162,39,0.08)" : "transparent",
            marginBottom: SP[4],
          }}
          onClick={() => document.getElementById("upload-input-portal")?.click()}
        >
          <div style={{ fontSize: TYPE["2xl"], marginBottom: SP[2] }}>📂</div>
          <div
            style={{
              fontSize: TYPE.base,
              color: "rgba(255,255,255,0.75)",
              fontWeight: TYPE.semibold,
            }}
          >
            Arraste os arquivos aqui ou clique para selecionar
          </div>
          <div style={{ fontSize: TYPE.xs, color: "rgba(255,255,255,0.4)", marginTop: SP[1] }}>
            PDF, CSV, XLSX, OFX · máx. 20 MB cada
          </div>
          <input
            id="upload-input-portal"
            type="file"
            multiple
            accept=".pdf,.csv,.xlsx,.xls,.ofx"
            style={{ display: "none" }}
            onChange={(e) => handleFiles(e.target.files!)}
          />
        </div>

        {/* File list com status por arquivo */}
        {files.length > 0 && (
          <div style={{ marginBottom: SP[4] }}>
            {files.map((f, i) => {
              const st = fileStatuses[f.name];
              const icon =
                st === "done"
                  ? "✅"
                  : st?.startsWith("error")
                    ? "❌"
                    : st === "uploading"
                      ? "⏳"
                      : "📄";
              const color =
                st === "done"
                  ? "#86EFAC"
                  : st?.startsWith("error")
                    ? "#FCA5A5"
                    : "rgba(255,255,255,0.85)";
              const errMsg = st?.startsWith("error") ? st.replace("error: ", "") : null;
              return (
                <div
                  key={i}
                  style={{
                    background: "rgba(255,255,255,0.07)",
                    borderRadius: RADIUS.sm,
                    padding: `${SP[2]} ${SP[3]}`,
                    marginBottom: SP[1],
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span
                      style={{
                        fontSize: TYPE.sm,
                        color,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                      }}
                    >
                      {icon} {f.name}
                    </span>
                    {!sending && !st && (
                      <button
                        onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "rgba(255,255,255,0.4)",
                          cursor: "pointer",
                          fontSize: TYPE.md,
                          padding: `0 0 0 ${SP[2]}`,
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {errMsg && (
                    <div style={{ fontSize: TYPE.xs, color: "#FCA5A5", marginTop: SP[1] }}>
                      {errMsg}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Corretoras */}
        <div style={{ display: "flex", gap: SP[2], marginBottom: SP[6] }}>
          {["XP Investimentos", "BTG Pactual", "Banco Inter", "Avenue Securities"].map((b) => (
            <div
              key={b}
              style={{
                flex: 1,
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: RADIUS.sm,
                padding: `${SP[2]} ${SP[1]}`,
                textAlign: "center",
                fontSize: TYPE.xs,
                color: "rgba(255,255,255,0.6)",
                lineHeight: TYPE.normal,
              }}
            >
              {b}
            </div>
          ))}
        </div>

        {/* Buttons */}
        <button
          onClick={handleSend}
          disabled={sending}
          style={{
            width: "100%",
            background: LIGHT.gold,
            color: LIGHT.navy,
            border: "none",
            borderRadius: RADIUS.md,
            padding: `${SP[3]} 0`,
            fontWeight: TYPE.heavy,
            fontSize: TYPE.lg,
            cursor: sending ? "wait" : "pointer",
            marginBottom: SP[2],
            opacity: sending ? 0.8 : 1,
          }}
        >
          {sending
            ? "Enviando…"
            : files.length > 0
              ? `Enviar ${files.length} arquivo${files.length > 1 ? "s" : ""}`
              : "Enviar extratos"}
        </button>
        <button
          onClick={onDone}
          style={{
            width: "100%",
            background: "transparent",
            color: "rgba(255,255,255,0.45)",
            border: "none",
            padding: `${SP[2]} 0`,
            fontSize: TYPE.sm,
            cursor: "pointer",
          }}
        >
          Pular por agora — farei isso depois
        </button>
      </div>

      <div style={{ marginTop: SP[5], fontSize: TYPE.xs, color: "rgba(255,255,255,0.25)" }}>
        Seus dados são protegidos pela LGPD e armazenados em servidores no Brasil.
      </div>
    </div>
  );
}
